import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    LeadSummarySectionBody,
    formatLeadSummaryTimestamp,
    partitionLeadSummary
} from "./leadSummaryDynamic";
import { toTitleCaseLabel } from "./leadSummaryLabels";
import { LeadRefreshIcon } from "./leadRefreshIcon";

const NAV_ITEMS = [
    { id: "lead-section-overall", label: "Synopsis" },
    { id: "lead-section-situation", label: "Situation" },
    { id: "lead-section-background", label: "Background" },
    { id: "lead-section-assessment", label: "Assessment" }
];

const SECTION_ENTER_OFFSET = 32;

function valueHasRenderableContent(value) {
    if (value == null) {
        return false;
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value).trim().length > 0;
    }
    if (Array.isArray(value)) {
        return value.some((item) => valueHasRenderableContent(item));
    }
    if (typeof value === "object") {
        return Object.keys(value).some((k) => valueHasRenderableContent(value[k]));
    }
    return false;
}

function GoTopIcon() {
    return (
        <span className="lead-summary-gotop__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12 19V5M5 12l7-7 7 7"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );
}

export function LeadSummaryWorkspace({
    leadSummary,
    lastUpdated,
    onRefresh,
    isRefreshing = false
}) {
    const scrollRef = useRef(null);
    const navRef = useRef(null);
    const navButtonRefs = useRef({});
    const programmaticNavRef = useRef(null);
    const scrollEndTimerRef = useRef(null);
    const [showGoTop, setShowGoTop] = useState(false);
    const [sidebarTab, setSidebarTab] = useState("questions");
    const [activeNavId, setActiveNavId] = useState(NAV_ITEMS[0].id);
    const [navIndicator, setNavIndicator] = useState({ left: 0, width: 0 });

    const partitioned = useMemo(() => partitionLeadSummary(leadSummary), [leadSummary]);
    const updatedLabel = formatLeadSummaryTimestamp(lastUpdated);

    const hasOverall =
        Boolean(partitioned.overallCallout) ||
        valueHasRenderableContent(partitioned.overallExtra);
    const hasSituation = valueHasRenderableContent(partitioned.situation);
    const hasBackground = valueHasRenderableContent(partitioned.background);
    const hasAssessment = valueHasRenderableContent(partitioned.assessment);
    const hasOther = partitioned.otherSections.some(([, v]) => valueHasRenderableContent(v));
    const hasMainContent =
        hasOverall || hasSituation || hasBackground || hasAssessment || hasOther;

    const totalQuestions = partitioned.keyQuestionGroups.reduce(
        (sum, g) => sum + g.questions.length,
        0
    );

    // Member summary has a flat key-question list (no POF accordion groups).

    const visibleNav = NAV_ITEMS.filter((item) => {
        if (item.id === "lead-section-overall") return hasOverall;
        if (item.id === "lead-section-situation") return hasSituation;
        if (item.id === "lead-section-background") return hasBackground;
        if (item.id === "lead-section-assessment") return hasAssessment;
        return true;
    });

    const updateActiveNavFromScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }

        let nextActiveId = visibleNav[0]?.id || null;
        for (const item of visibleNav) {
            const section = el.querySelector(`#${item.id}`);
            if (!section) {
                continue;
            }
            const topOffset =
                section.getBoundingClientRect().top - el.getBoundingClientRect().top;
            if (topOffset <= SECTION_ENTER_OFFSET) {
                nextActiveId = item.id;
            } else {
                break;
            }
        }
        if (nextActiveId && nextActiveId !== activeNavId) {
            setActiveNavId(nextActiveId);
        }
    }, [visibleNav, activeNavId]);

    const updateGoTopVisibility = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            setShowGoTop(false);
            return;
        }
        // Show as soon as the vertical scrollbar moves ~10px from the top.
        setShowGoTop(el.scrollTop >= 20);
    }, []);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        updateGoTopVisibility();

        if (programmaticNavRef.current) {
            window.clearTimeout(scrollEndTimerRef.current);
            scrollEndTimerRef.current = window.setTimeout(() => {
                programmaticNavRef.current = null;
                updateActiveNavFromScroll();
                updateGoTopVisibility();
            }, 140);
            return;
        }
        updateActiveNavFromScroll();
    }, [updateActiveNavFromScroll, updateGoTopVisibility]);

    const scrollToSection = (sectionId) => {
        const root = scrollRef.current;
        if (!root) {
            return;
        }
        const target = root.querySelector(`#${sectionId}`);
        if (!target) {
            return;
        }
        programmaticNavRef.current = sectionId;
        window.clearTimeout(scrollEndTimerRef.current);
        setActiveNavId(sectionId);
        const top =
            target.getBoundingClientRect().top -
            root.getBoundingClientRect().top +
            root.scrollTop -
            8;
        root.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        scrollEndTimerRef.current = window.setTimeout(() => {
            programmaticNavRef.current = null;
            updateActiveNavFromScroll();
            updateGoTopVisibility();
        }, 140);
    };

    useLayoutEffect(() => {
        const updateIndicator = () => {
            const nav = navRef.current;
            const button = navButtonRefs.current[activeNavId];
            if (!nav || !button) {
                return;
            }
            setNavIndicator({
                left: button.offsetLeft,
                width: button.offsetWidth
            });
        };
        updateIndicator();
        window.addEventListener("resize", updateIndicator);
        return () => window.removeEventListener("resize", updateIndicator);
    }, [activeNavId]);

    useEffect(
        () => () => {
            window.clearTimeout(scrollEndTimerRef.current);
        },
        []
    );

    const scrollToTop = () => {
        scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    useEffect(() => {
        if (!visibleNav.some((item) => item.id === activeNavId) && visibleNav[0]) {
            setActiveNavId(visibleNav[0].id);
        }
    }, [hasOverall, hasSituation, hasBackground, hasAssessment, activeNavId]);

    return (
        <div className="lead-summary-workspace">
            <div className="lead-summary-main">
                <div className="lead-summary-main__header">
                    <h2 className="lead-summary-main__title">Summary</h2>
                    <div className="lead-summary-main__meta">
                        {updatedLabel ? (
                            <span className="lead-summary-main__updated">
                                Last updated on: {updatedLabel}
                            </span>
                        ) : null}
                        <button
                            type="button"
                            className="lead-summary-main__update"
                            onClick={onRefresh}
                            disabled={isRefreshing}
                        >
                            {isRefreshing
                                ? hasMainContent
                                    ? "Updating..."
                                    : "Generating..."
                                : hasMainContent
                                  ? "Update Now"
                                  : "Generate Now"}
                            <LeadRefreshIcon spinning={false} />
                        </button>
                    </div>
                </div>

                {visibleNav.length > 0 ? (
                    <nav
                        className="lead-summary-main__nav"
                        aria-label="Synopsis sections"
                        ref={navRef}
                    >
                        {visibleNav.map((item) => (
                            <button
                                key={item.id}
                                ref={(node) => {
                                    if (node) {
                                        navButtonRefs.current[item.id] = node;
                                    } else {
                                        delete navButtonRefs.current[item.id];
                                    }
                                }}
                                type="button"
                                className={
                                    activeNavId === item.id
                                        ? "lead-summary-main__nav-btn is-active"
                                        : "lead-summary-main__nav-btn"
                                }
                                onClick={() => scrollToSection(item.id)}
                            >
                                {item.label}
                            </button>
                        ))}
                        <span
                            className="lead-summary-main__nav-indicator"
                            aria-hidden="true"
                            style={{
                                width: navIndicator.width,
                                transform: `translateX(${navIndicator.left}px)`
                            }}
                        />
                    </nav>
                ) : null}

                <div
                    className="lead-summary-main__scroll"
                    ref={scrollRef}
                    onScroll={handleScroll}
                >
                    {!hasMainContent ? (
                        <div className="lead-summary-empty-inline lead-summary-empty-inline--not-available">
                            <p>
                                <strong>Not Available:</strong> “Summary not available yet.
                                Please click “generate now” to generate the summary.”
                            </p>
                        </div>
                    ) : (
                        <>
                            {hasOverall ? (
                                <section
                                    className="lead-summary-block-section"
                                    id="lead-section-overall"
                                    aria-label="Synopsis"
                                >
                                    <h3 className="lead-summary-block-section__title">
                                        Synopsis
                                    </h3>
                                    {partitioned.overallCallout ? (
                                        <div className="lead-summary-callout">
                                            <p>{partitioned.overallCallout}</p>
                                        </div>
                                    ) : null}
                                    {partitioned.overallExtra ? (
                                        <LeadSummarySectionBody
                                            value={partitioned.overallExtra}
                                            depth={1}
                                        />
                                    ) : null}
                                </section>
                            ) : null}

                            {hasSituation ? (
                                <section
                                    className="lead-summary-block-section"
                                    id="lead-section-situation"
                                    aria-label="Situation"
                                >
                                    <h3 className="lead-summary-block-section__title">Situation</h3>
                                    <LeadSummarySectionBody value={partitioned.situation} />
                                </section>
                            ) : null}

                            {hasBackground ? (
                                <section
                                    className="lead-summary-block-section"
                                    id="lead-section-background"
                                    aria-label="Background"
                                >
                                    <h3 className="lead-summary-block-section__title">Background</h3>
                                    <LeadSummarySectionBody value={partitioned.background} />
                                </section>
                            ) : null}

                            {hasAssessment ? (
                                <section
                                    className="lead-summary-block-section"
                                    id="lead-section-assessment"
                                    aria-label="Assessment"
                                >
                                    <h3 className="lead-summary-block-section__title">Assessment</h3>
                                    <LeadSummarySectionBody value={partitioned.assessment} />
                                </section>
                            ) : null}

                            {partitioned.otherSections.map(([title, value]) =>
                                valueHasRenderableContent(value) ? (
                                    <section
                                        className="lead-summary-block-section"
                                        key={title}
                                        aria-label={title}
                                    >
                                        <h3 className="lead-summary-block-section__title">
                                            {toTitleCaseLabel(title)}
                                        </h3>
                                        <LeadSummarySectionBody value={value} />
                                    </section>
                                ) : null
                            )}
                        </>
                    )}
                </div>

                {showGoTop ? (
                    <button
                        type="button"
                        className="lead-summary-gotop"
                        onClick={scrollToTop}
                        aria-label="Go to top of summary"
                        title="Go on top"
                    >
                        <GoTopIcon />
                    </button>
                ) : null}
            </div>

            <aside className="lead-summary-aside" aria-label="Key questions and recommendations">
                <div className="lead-summary-aside__tabs" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={sidebarTab === "questions"}
                        className={
                            sidebarTab === "questions"
                                ? "lead-summary-aside__tab is-active"
                                : "lead-summary-aside__tab"
                        }
                        onClick={() => setSidebarTab("questions")}
                    >
                        Key Questions
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={sidebarTab === "recommendations"}
                        className={
                            sidebarTab === "recommendations"
                                ? "lead-summary-aside__tab is-active"
                                : "lead-summary-aside__tab"
                        }
                        onClick={() => setSidebarTab("recommendations")}
                    >
                        Recommendation
                    </button>
                </div>

                <div className="lead-summary-aside__body">
                    {sidebarTab === "questions" ? (
                        partitioned.keyQuestionGroups.length === 0 ? (
                            <p className="lead-summary-empty-inline">No key questions available.</p>
                        ) : (
                            <>
                                <div className="lead-summary-aside__count-strip">
                                    <p className="lead-summary-aside__count">
                                        Total Questions: <strong>{totalQuestions}</strong>
                                    </p>
                                </div>
                                <div className="lead-summary-questions">
                                    <ol className="lead-summary-questions__flat">
                                        {partitioned.keyQuestionGroups.flatMap((group) =>
                                            group.questions.map((q, idx) => (
                                                <li key={`${group.group}-${idx}`}>{q}</li>
                                            ))
                                        )}
                                    </ol>
                                </div>
                            </>
                        )
                    ) : partitioned.recommendations.length === 0 ? (
                        <p className="lead-summary-empty-inline">No recommendations available.</p>
                    ) : (
                        <ul className="lead-summary-recs">
                            {partitioned.recommendations.map((item, idx) => (
                                <li key={`rec-${idx}`} className="lead-summary-recs__item">
                                    {item.title ? (
                                        <>
                                            <strong>{item.title}:</strong> {item.body}
                                        </>
                                    ) : (
                                        item.body
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </aside>

            <div
                className="lead-summary-ai-disclaimer"
                role="note"
                aria-label="AI summary disclaimer"
            >
                <span className="lead-summary-ai-disclaimer__icon" aria-hidden="true">
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M6.58333 8.91667V6.58333M6.58333 4.25H6.58917M12.4167 6.58333C12.4167 9.805 9.805 12.4167 6.58333 12.4167C3.36167 12.4167 0.75 9.805 0.75 6.58333C0.75 3.36167 3.36167 0.75 6.58333 0.75C9.805 0.75 12.4167 3.36167 12.4167 6.58333Z"
                            stroke="#C7872B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                <p className="lead-summary-ai-disclaimer__text">
                    This summary is generated by AI based on available data and is intended for
                    informational purposes only. Please verify the information against the source
                    records before making decisions.
                </p>
            </div>
        </div>
    );
}

LeadSummaryWorkspace.propTypes = {
    leadSummary: PropTypes.object,
    lastUpdated: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onRefresh: PropTypes.func,
    isRefreshing: PropTypes.bool
};
